import React from 'react';
import { Progress } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobeAmericas } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import {
    createDataSource, createGridOptions, DataGrid, LinkCellRenderer, TextFilterDefaults, indexCellRenderer,
} from '../common/dataGrid/DataGrid';
import DataGridSelectFilter from '../common/dataGrid/DataGridSelectFilter';
import CommonContext, { ApiRoutes, AppNavPaths } from '../Common';
import { PageHeading, PageWrap } from '../common/forms/FormElements';
import DataGridToolbar from '../common/dataGrid/DataGridToolbar';
import DataGridSelectFloatingFilter from '../common/dataGrid/DataGridSelectFloatingFilter';

class USCountyGroupsIndex extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            rowData: [],
        };

        this.onAddCountyGroup = this.onAddCountyGroup.bind(this);
    }

    componentDidMount() {
        return this.populateState();
    }

    onAddCountyGroup = () => {
        this.context.history.push(`${AppNavPaths.USCountyGroupNew}`);
    };

    async populateState() {
        const [groupTypes, usStates, contractUnionWorkTypes] = await Promise.all([
            httpClient.js(ApiRoutes.USCounties.groupTypes()),
            httpClient.js(ApiRoutes.typeAheads.USStates()),
            httpClient.js(ApiRoutes.typeAheads.contractUnionWorkTypes()),
        ]);

        const gridOptions = createGridOptions(this);

        const workTypeFilterParams = {
            suppressFilterButton: true,
            options: contractUnionWorkTypes.filter((x) => x.isUnionWork),
            optionsLabel: 'label',
            optionsValue: 'value',
        };

        const groupTypeFilterParams = {
            suppressFilterButton: true,
            options: groupTypes,
            optionsLabel: 'description',
            optionsValue: 'id',
        };

        const usStateFilterParams = {
            suppressFilterButton: true,
            options: usStates,
            optionsLabel: 'label',
            optionsValue: 'id',
        };

        gridOptions.components = {
            selectFilter: DataGridSelectFilter,
            selectFloatingFilter: DataGridSelectFloatingFilter,
            nameRenderer: LinkCellRenderer,
        };

        gridOptions.columnDefs = [
            {
                flex: 0,
                maxWidth: 80,
                headerName: '',
                valueGetter: 'node.id',
                sortable: false,
                cellRenderer: indexCellRenderer,
            },
            {
                colId: 'Name',
                sortable: false,
                headerName: 'Name',
                field: 'description',
                cellRenderer: 'nameRenderer',
                cellRendererParams: {
                    to: (id) => `${AppNavPaths.USCountyGroup}/${id}`,
                    nameField: 'description',
                    idField: 'id',
                    title: 'View this County Group',
                },
                filter: 'agTextColumnFilter',
                filterParams: TextFilterDefaults,
                floatingFilter: true,
                floatingFilterComponentParams: {
                    suppressFilterButton: true,
                },
            },
            {
                colId: 'USState',
                headerName: 'State',
                sortable: false,
                flex: 1.5,
                field: 'usStateName',
                tooltipField: 'usStateName',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: usStateFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: usStateFilterParams,
            },
            {
                colId: 'GroupType',
                headerName: 'Type',
                sortable: false,
                flex: 1.5,
                field: 'groupType',
                tooltipField: 'groupType',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: groupTypeFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: groupTypeFilterParams,
            },
            {
                colId: 'counties',
                headerName: 'Counties',
                sortable: false,
                flex: 1.5,
                field: 'countiesList',
            },
            {
                colId: 'WorkType',
                headerName: 'Applicable Work Type(s)',
                sortable: false,
                flex: 1.5,
                field: 'contractUnionWorkTypeList',
                tooltipField: 'contractUnionWorkTypeList',
                filter: 'selectFilter',
                floatingFilter: true,
                filterParams: workTypeFilterParams,
                floatingFilterComponent: 'selectFloatingFilter',
                floatingFilterComponentParams: workTypeFilterParams,
            },
            {
                colId: 'PayRate',
                headerName: 'Pay Rate',
                sortable: false,
                flex: 1.5,
                field: 'workTypePayRatesList',
                valueFormatter: (params) => params.value?.length
                    ? params.value
                    : 'N/A',
            },
        ];

        const dataSource = createDataSource(ApiRoutes.USCounties.groups(), gridOptions);

        this.setState({
            loading: false,
            gridOptions,
            dataSource,
        });
    }

    render() {
        if (this.state.loading) return (<Progress />);

        const { rowData, gridOptions } = this.state;

        return (
            <PageWrap>
                <PageHeading>
                    <FontAwesomeIcon
                        icon={faGlobeAmericas}
                        className="mr-2 text-muted"
                    />
                    <span>County Groups/Zones</span>
                </PageHeading>
                <DataGridToolbar
                    entity="county_group"
                    gridApi={this.state.gridApi}
                    dataSource={this.state.dataSource}
                    onAdd={this.onAddCountyGroup}
                    addLabel="Add County Group"
                    serverExportDisabled={false}
                />
                <DataGrid
                    domLayout="normal"
                    rowData={rowData}
                    gridOptions={gridOptions}
                    gridStatus={this.state.gridStatus}
                />
            </PageWrap>
        );
    }
}
export default USCountyGroupsIndex;
